import React, { useRef, useState, useEffect } from "react";
import { motion, AnimatePresence, useAnimationControls } from "framer-motion";
import { shakeVariants } from "../animations/variants";
import { GAME_CONFIG } from "../config/gameConfig";
import { UI_STRINGS } from "../strings/uiStrings";
import { EggDisplay } from "./EggDisplay";
import { CountdownOverlay } from "./CountdownOverlay";
import { ImpactEffect } from "./ImpactEffect";
import { WinScreen } from "./WinScreen";
import { LoseScreen } from "./LoseScreen";
import { TieScreen } from "./TieScreen";
import type { GameState, Player } from "../types";
import { RoundResultOverlay } from "./RoundResultOverlay";
import { useEggFightAudio } from "../hooks/useEggFightAudio";
import { ReadyScreen } from "./ReadyScreen";

interface ArenaScreenProps {
  /** The local player — always displayed on the right as "YOUR EGG". */
  player1: Player;
  /** The opponent — always displayed on the left as "ENEMY EGG". */
  player2: Player;
  state: GameState;
  readyKey: string;
  /** Receives the local player object so callers know exactly who confirmed. */
  onPlayerReady: (player: Player) => void;
  readyCount: number;
}

/**
 * ArenaScreen — the active match view.
 *
 * Owns all fight-phase presentation logic: clash timing, screen shakes,
 * egg displays, round overlays, and end screens.
 *
 * This is the identity boundary — player1 is always the local player.
 * ReadyScreen stays player-agnostic; handleReady injects player1 here.
 *
 * Transitions to WinScreen, LoseScreen, or TieScreen when the match ends,
 * based on whether the local player (player1) won or lost.
 */
export function ArenaScreen({
  player1,
  player2,
  state,
  readyKey,
  onPlayerReady,
  readyCount,
}: ArenaScreenProps): React.ReactElement {
  const shakeControls = useAnimationControls();
  const hasShakenRef = useRef(false);

  // player1 is always the local player — identity is established here, once.
  // ReadyScreen stays player-agnostic; we inject the player at this boundary.
  const me = player1;
  const enemy = player2;
  const handleReady = (): void => onPlayerReady(player1);

  useEggFightAudio(state, player1.id);

  const [clashFlash, setClashFlash] = useState<0 | 1 | 2 | 3>(0);
  const fightTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  const { phase } = state;

  // Schedule clash flashes and per-clash screen shakes when fight phase starts.
  // Clears all timers and resets on any phase change away from "fighting".
  useEffect(() => {
    fightTimersRef.current.forEach(clearTimeout);
    fightTimersRef.current = [];

    if (phase !== "fighting") {
      setClashFlash(0);
      return;
    }

    const { clash1Ms, clash2Ms, clash3Ms } = GAME_CONFIG.animation.fightClashes;
    const flashDuration = 400;

    const addTimer = (fn: () => void, delay: number): void => {
      const t = setTimeout(fn, delay);
      fightTimersRef.current.push(t);
    };

    addTimer(() => {
      setClashFlash(1);
      shakeControls.start("shake1").then(() => shakeControls.start("idle"));
      addTimer(() => setClashFlash(0), flashDuration);
    }, clash1Ms);

    addTimer(() => {
      setClashFlash(2);
      shakeControls.start("shake2").then(() => shakeControls.start("idle"));
      addTimer(() => setClashFlash(0), flashDuration);
    }, clash2Ms);

    addTimer(() => {
      setClashFlash(3);
      shakeControls.start("shake3").then(() => shakeControls.start("idle"));
      addTimer(() => setClashFlash(0), flashDuration);
    }, clash3Ms);

    return () => {
      fightTimersRef.current.forEach(clearTimeout);
      fightTimersRef.current = [];
    };
    // shakeControls is stable; phase is the only re-scheduling dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Impact phase screen shake (final result reveal)
  if (phase === "impact" && !hasShakenRef.current) {
    hasShakenRef.current = true;
    shakeControls.start("shake3").then(() => shakeControls.start("idle"));
  }
  if (phase === "countdown") {
    hasShakenRef.current = false;
  }

  // ── Derived state ──────────────────────────────────────────────────────────

  const { roles, roundWinnerId, roundLoserId, wins, countdownValue } = state;

  // player2 = enemy = left side; player1 = local player = right side
  const leftPlayer = player2;
  const rightPlayer = player1;
  const leftRole = roles.attacker === player2.id ? "attacker" : "defender";
  const rightRole = roles.attacker === player1.id ? "attacker" : "defender";

  const leftIsWinner = roundWinnerId === leftPlayer.id;
  const leftIsLoser = roundLoserId === leftPlayer.id;
  const rightIsWinner = roundWinnerId === rightPlayer.id;
  const rightIsLoser = roundLoserId === rightPlayer.id;

  // During fight contact, the attacker panel sits above the defender panel
  const isFightContact = phase === "fighting" || phase === "impact";
  const leftPanelZ = isFightContact ? (leftRole === "attacker" ? 2 : 1) : 1;
  const rightPanelZ = isFightContact ? (rightRole === "attacker" ? 2 : 1) : 1;

  // Impact effect trigger key — remounts the animation cleanly on each event
  const impactTriggerKey =
    clashFlash > 0
      ? `clash-${clashFlash}`
      : phase === "impact"
        ? "final-impact"
        : "idle";
  const impactIntensity: 1 | 2 | 3 =
    clashFlash === 1 ? 1 : clashFlash === 2 ? 2 : 3;

  // ── End-screen logic ───────────────────────────────────────────────────────
  // player1 is always the local player; determines which screen to show.

  const isGameOver = phase === "game-over";
  const isTie = isGameOver && state.gameWinnerId === null;
  const won = isGameOver && state.gameWinnerId === me.id;
  const lost = isGameOver && !won && !isTie;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <motion.div
      className="relative flex"
      animate={shakeControls}
      variants={shakeVariants}
      initial="idle"
      style={{ willChange: "transform", flex: 1, minHeight: 0 }}
    >
      {!isGameOver && (
        <>
          {/* Left egg — ENEMY EGG */}
          <EggDisplay
            player={leftPlayer}
            role={leftRole}
            side="left"
            phase={phase}
            isWinner={leftIsWinner}
            isLoser={leftIsLoser}
            roundHistory={state.roundHistory}
            sideLabel={UI_STRINGS.roles.enemyEgg}
            panelZIndex={leftPanelZ}
          />

          {/* Center divider line (subtle) */}
          <div
            style={{
              position: "absolute",
              left: "50%",
              top: "15%",
              bottom: "15%",
              width: 1,
              background:
                "linear-gradient(to bottom, transparent, rgba(255,255,255,0.06), transparent)",
              transform: "translateX(-50%)",
              zIndex: 1,
            }}
          />

          {/* Right egg — YOUR EGG */}
          <EggDisplay
            player={rightPlayer}
            role={rightRole}
            side="right"
            phase={phase}
            isWinner={rightIsWinner}
            isLoser={rightIsLoser}
            roundHistory={state.roundHistory}
            sideLabel={UI_STRINGS.roles.yourEgg}
            panelZIndex={rightPanelZ}
          />

          {/* Impact star — fires on each clash and the final impact phase */}
          <ImpactEffect
            key={impactTriggerKey}
            visible={phase === "impact" || clashFlash > 0}
            triggerKey={impactTriggerKey}
            intensity={impactIntensity}
          />
        </>
      )}

      {/* Ready-up screen — shown before countdown begins */}
      <AnimatePresence>
        {phase === "waiting_ready" && (
          <ReadyScreen
            key="ready"
            readyKey={readyKey}
            onPlayerReady={handleReady}
            readyCount={readyCount}
          />
        )}
      </AnimatePresence>

      {/* Countdown numbers */}
      <AnimatePresence>
        {phase === "countdown" && (
          <CountdownOverlay key="countdown" value={countdownValue} />
        )}
      </AnimatePresence>

      {/* Round result overlay */}
      <AnimatePresence>
        {phase === "round-result" && roundWinnerId && (
          <RoundResultOverlay
            key="roundresult"
            winnerName={
              roundWinnerId === player1.id ? player1.name : player2.name
            }
            winnerId={roundWinnerId}
            player1Id={player1.id}
            player2Id={player2.id}
            roundHistory={state.roundHistory}
          />
        )}
      </AnimatePresence>

      {/* End screens — only one is shown at a time */}
      <AnimatePresence>
        {won && <WinScreen key="win" me={me} opponent={enemy} wins={wins} />}
        {lost && <LoseScreen key="lose" me={me} opponent={enemy} wins={wins} />}
        {isTie && (
          <TieScreen
            key="tie"
            player1={player1}
            player2={player2}
            wins={wins}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}
